import React from "react";
import { appendOwnerJarvisConversationMessage, clearOwnerJarvisConversationJournal, ownerJarvisConversationContext } from "../jarvis/ownerJarvisConversationJournal";
import {
  Box,
  Button,
  Chip,
  GlobalStyles,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import {
  cleanFieldCommand,
  findFieldByVoice,
  isAffirmative,
  isNegative,
  matchVoiceOption,
  normalizeVoiceText,
  parseVoiceDateTime,
  parseVoiceNumber,
  parseVoicePhone,
} from "./ownerVoiceParsers";
import { isOwnerLocalSttSupported, OwnerLocalSpeechRecognition } from "./ownerLocalSpeechToText";
import { clearOwnerJarvisStoredTurns } from "../jarvis/ownerJarvisStorage";
import { isOwnerJarvisBacklogAcknowledgement } from "../jarvis/capabilities/ownerJarvisImprovementCapabilities";
import { ownerApi } from "../../../../api/owner";

const OwnerVoiceContext = React.createContext(null);

const COMMANDS = {
  next: ["siguiente", "seguir", "segui", "sigamos", "continua", "continuar", "omitir", "saltear", "pasar", "pasemos al siguiente"],
  previous: ["anterior", "volver al campo anterior", "campo anterior", "atras", "volve uno", "volver uno"],
  missing: ["que falta", "que me falta", "campos pendientes", "faltantes", "que queda", "falta algo"],
  save: ["guardar", "guarda", "guardalo", "guardala", "confirmar", "confirmo", "terminar", "finalizar", "listo", "mandale guardar"],
  cancel: ["cancelar", "cancela", "cancelalo", "cancelala", "cerrar formulario", "salir del formulario", "deja esto", "dejalo", "solta esto"],
  stop: ["parar voz", "detener voz", "silencio", "deja de escuchar", "terminar conversacion"],
  powerOff: ["apagate", "apagar jarvis", "dormite", "dormi jarvis", "dormite jarvis", "bloqueate", "bloqueate jarvis", "desactivar jarvis", "adios jarvis", "chau jarvis", "chao jarvis", "hasta luego jarvis"],
  powerOn: ["encendete", "encender jarvis", "despertar jarvis", "despierta jarvis", "despertate jarvis", "activar jarvis"],
  startForm: ["completar formulario", "completar por voz", "rellenar formulario", "llenar formulario", "ayudame con este formulario"],
  options: ["que puedo hacer", "que se puede hacer", "opciones", "acciones", "ayuda de voz", "comandos", "ayudame", "que puedo decir"],
  repeat: ["repeti", "repetir", "repite", "otra vez", "decilo de nuevo", "decimelo de nuevo", "no entendi", "que dijiste", "como dijiste"],
};

function matchesCommand(text, variants = []) {
  const normalized = normalizeVoiceText(text);
  return variants.some((item) => normalized === item || normalized.startsWith(`${item} `));
}

const OWNER_JARVIS_WAKE_STATE_KEY = "wp-owner-jarvis-wake-state-v1";
const VOICE_RESTART_BASE_DELAY_MS = 180;
const VOICE_RESTART_MAX_DELAY_MS = 5000;
const VOICE_RESTART_HEALTHY_SESSION_MS = 4500;
const VOICE_RESTART_REPORT_AFTER = 5;
const VOICE_RESTART_REPORT_COOLDOWN_MS = 30000;

function isJarvisWakePhrase(value = "") {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  if (/^(?:hola|hey|oye|che)\s+(?:jarvis|yarvis|jervis)\b/.test(text)) return true;
  return matchesCommand(text, COMMANDS.powerOn);
}

function jarvisWakeRemainder(value = "") {
  const text = normalizeVoiceText(value);
  return text
    .replace(/^(?:hola|hey|oye|che)\s+(?:jarvis|yarvis|jervis)\b\s*/, "")
    .trim();
}

function isJarvisSleepPhrase(value = "") {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  if (/^(?:chau|chao|adios|hasta luego)(?:\s+jarvis)?$/.test(text)) return true;
  return matchesCommand(text, COMMANDS.powerOff);
}

function isJarvisGreeting(value = "") {
  const text = normalizeVoiceText(value);
  return /^(?:hola|buenas|hola jarvis|buenas jarvis|que haces jarvis)$/.test(text);
}

function isJarvisHowAreYou(value = "") {
  const text = normalizeVoiceText(value);
  return /^(?:como estas|como estas jarvis|todo bien|todo bien jarvis)$/.test(text);
}

function isJarvisThanks(value = "") {
  const text = normalizeVoiceText(value);
  return /^(?:gracias|gracias jarvis|muchas gracias|listo gracias|perfecto gracias)$/.test(text)
    || isOwnerJarvisBacklogAcknowledgement(value);
}

function isJarvisWhereWereWe(value = "") {
  const text = normalizeVoiceText(value);
  return /^(?:donde estabamos|en que estabamos|que estabamos haciendo|por donde ibamos|que estabamos viendo|me perdi|me perdi jarvis|recordame donde estabamos|retomemos|que onda|que onda jarvis)$/.test(text);
}

function preferredOwnerSpanishVoice() {
  if (typeof window === "undefined" || !window.speechSynthesis?.getVoices) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  const score = (voice) => {
    const lang = String(voice?.lang || "").toLowerCase();
    const name = String(voice?.name || "").toLowerCase();
    let points = 0;
    if (lang === "es-ar") points += 100;
    else if (lang.startsWith("es-")) points += 70;
    else if (lang === "es") points += 55;
    if (/natural|neural|premium|enhanced/.test(name)) points += 25;
    if (/google|microsoft|apple/.test(name)) points += 8;
    if (voice?.localService) points += 2;
    return points;
  };
  return [...voices].sort((a, b) => score(b) - score(a))[0] || null;
}

function cleanOwnerSpokenText(value = "") {
  return String(value || "")
    .replace(/\s+/g, " ")
    .replace(/\b(?:no hace falta memorizar comandos?|volvemos al mapa y elegimos desde acá)\.?/gi, "")
    .replace(/\s+([,.;!?])/g, "$1")
    .trim();
}

function hasDetailedShortcutPayload(raw, shortcutKey = "") {
  const normalized = normalizeVoiceText(raw);
  if (!normalized) return false;
  if (/[0-9@]/.test(String(raw || ""))) return true;
  const generic = new Set([
    "yo", "quiero", "quisiera", "necesito", "por", "favor", "un", "una", "el", "la", "los", "las", "de", "del", "para", "que",
    "anotar", "anota", "anotá", "cargar", "carga", "cargá", "registrar", "registra", "registrá", "agregar", "agrega", "agregá", "sumar",
    "nuevo", "nueva", "hacer", "crear",
  ]);
  const nounsByShortcut = {
    finance: new Set(["gasto", "ingreso", "movimiento", "transferencia", "compra", "pago"]),
    commitment: new Set(["tarea", "seguimiento", "recordatorio", "turno", "pendiente"]),
    proposal: new Set(["propuesta", "presupuesto", "cotizacion", "cotización"]),
  };
  const nouns = nounsByShortcut[shortcutKey] || new Set();
  const remainder = normalized.split(" ").filter((token) => token.length > 1 && !generic.has(token) && !nouns.has(token));
  return remainder.length >= 2;
}

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function visibleFields(config) {
  const values = config?.values || {};
  return (config?.fields || []).filter((field) => !field.when || field.when(values));
}

function requiredMissing(config) {
  const values = config?.values || {};
  return visibleFields(config).filter((field) => {
    const required = typeof field.required === "function" ? field.required(values) : field.required;
    return required && !hasValue(values[field.name]);
  });
}

function formatOptions(field) {
  if (field.speakOptions === false || !field.options?.length) return "";
  const labels = field.options.map((row) => row.label).filter(Boolean);
  if (!labels.length) return "";
  if (labels.length <= 4) return ` Opciones: ${labels.join(", ")}.`;
  return " Elegí una de las opciones visibles.";
}

function parseFieldValue(field, raw, config) {
  const clean = cleanFieldCommand(raw, field);
  if (field.parse) return field.parse(clean, config?.values || {});
  if (field.kind === "number") return parseVoiceNumber(clean);
  if (field.kind === "choice") return matchVoiceOption(clean, field.options || [])?.value ?? null;
  if (field.kind === "datetime") return parseVoiceDateTime(clean);
  if (field.kind === "phone") return parseVoicePhone(clean);
  if (field.kind === "email") return String(clean).replace(/\s+arroba\s+/i, "@").replace(/\s+punto\s+/i, ".").replace(/\s/g, "");
  return String(clean).trim();
}

function fieldValueLabel(field, value) {
  if (!hasValue(value)) return "vacío";
  if (field.kind === "choice") return field.options?.find((row) => String(row.value) === String(value))?.label || String(value);
  return String(value);
}


function looksLikePromptEcho(transcript, prompt) {
  const heard = normalizeVoiceText(transcript).split(" ").filter((word) => word.length > 1);
  const spoken = normalizeVoiceText(prompt).split(" ").filter((word) => word.length > 1);
  if (!heard.length || !spoken.length) return false;
  // Respuestas cortas como "pago mensual", "sí" o "gasto" son justo
  // las que queremos aceptar encima de la locución. Sólo tratamos como eco
  // una frase suficientemente larga y muy parecida a lo que el bot está leyendo.
  if (heard.length < 4) return false;
  const spokenSet = new Set(spoken);
  const overlap = heard.filter((word) => spokenSet.has(word)).length / heard.length;
  return overlap >= 0.86;
}


function stopMediaStream(stream) {
  try {
    stream?.getTracks?.().forEach((track) => {
      try { track.stop?.(); } catch { /* noop */ }
    });
  } catch { /* noop */ }
}

function microphoneErrorMessage(error) {
  const name = String(error?.name || "");
  if (name === "NotAllowedError" || name === "SecurityError") return "El navegador no me dejó abrir el audio real. Revisá el permiso del sitio y el permiso del sistema operativo.";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "No encontré ningún micrófono disponible en este equipo.";
  if (name === "NotReadableError" || name === "TrackStartError") return "El micrófono existe pero no se puede abrir. Puede estar siendo usado en exclusiva por otra aplicación.";
  if (name === "OverconstrainedError") return "El dispositivo de audio no cumple las restricciones solicitadas. Probá otro micrófono.";
  if (name === "AbortError") return "El navegador abortó la apertura del micrófono. Probá otra vez.";
  return `No pude abrir el audio real${name ? ` (${name})` : ""}.`;
}

function recognitionErrorMessage(code, microphoneVerified = false, detail = null) {
  const error = String(code || "");
  if (error === "audio-capture") return "No pude capturar audio real del micrófono. Voy a volver a validar el dispositivo.";
  if (error === "not-allowed") return microphoneVerified
    ? "El micrófono existe, pero el navegador bloqueó una nueva captura. Revisá el permiso del sitio."
    : "El navegador bloqueó el acceso al micrófono. Revisá el permiso del sitio y del sistema operativo.";
  if (error === "local-stt") {
    const reason = String(detail?.message || detail || "").trim();
    return `El motor local Whisper no pudo transcribir${reason ? `: ${reason}` : "."}`;
  }
  return `El reconocimiento local falló${error ? ` (${error})` : ""}. Probá otra vez.`;
}

function voiceTechnicalError(error) {
  if (!error) return "";
  const name = String(error?.name || "").trim();
  const message = String(error?.message || error || "").trim();
  return [name, message].filter(Boolean).join(": ").slice(0, 700);
}

function contextLabel(context) {
  const entity = context?.entity || context;
  return entity?.name || entity?.title || entity?.company || "esto";
}

function looksLikeGlobalEscapeIntent(value = "") {
  const text = normalizeVoiceText(value);
  const navigation = /\b(?:abr\w*|mostr\w*|mir\w*|llev\w*|ver|ir)\b/.test(text)
    && /\b(?:ficha|proyecto|proyectos|cliente|clientes|persona|rutina|rutinas|dia|fecha|movimientos|finanzas|calendario|agenda)\b/.test(text);
  const projectNote = /\b(?:anot\w*|apunt\w*|agreg\w*|carg\w*|registr\w*)\b/.test(text)
    && /\b(?:proyecto|frente|iniciativa)\b/.test(text);
  const financeView = /\b(?:mostr\w*|mir\w*|ver|list\w*)\b/.test(text)
    && /\b(?:entrada|entradas|ingreso|ingresos|pago|pagos|cobro|cobros|movimiento|movimientos)\b/.test(text);
  return navigation || projectNote || financeView;
}

function looksLikeExplicitGlobalActionHandoff(value = "") {
  const text = normalizeVoiceText(value);
  if (!text) return false;
  const directed = /\b(?:crear|crea|abrir|abre|abrime|mostrar|mostrame|ir|anda|vamos|volvamos|llevame|ver)\b/.test(text);
  const conciseNew = /^(?:nuevo|nueva)\b/.test(text) && text.split(" ").filter(Boolean).length <= 5;
  return directed || conciseNew;
}

function isOwnerHomeResetIntent(value = "") {
  const text = normalizeVoiceText(value).trim();
  return /^(?:inicio|menu principal|panel de hoy|volver al inicio|volver a inicio|ir al inicio|anda al inicio|vamos al inicio|navegar al inicio|navegar a inicio|volver a hoy|ir a hoy|navegar a hoy)$/.test(text);
}

function focusVoiceField(name) {
  if (typeof document === "undefined" || !name) return;
  document.querySelectorAll('[data-owner-voice-active="true"]').forEach((node) => node.removeAttribute("data-owner-voice-active"));
  const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(name) : name;
  const input = document.querySelector(`[data-owner-voice-field="${escaped}"]`);
  if (!input) return;
  const wrapper = input.closest(".MuiFormControl-root") || input.closest(".MuiTextField-root") || input.parentElement;
  wrapper?.setAttribute("data-owner-voice-active", "true");
  wrapper?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  const focusTarget = input.matches?.('[role="combobox"]') ? input : (wrapper?.querySelector?.('[role="combobox"]') || input);
  window.setTimeout(() => focusTarget?.focus?.(), 80);
}

function highlightOwnerSection(sectionId, { scroll = true, duration = 4200 } = {}) {
  if (typeof document === "undefined" || !sectionId) return false;
  document.querySelectorAll('[data-owner-jarvis-focus="true"]').forEach((node) => node.removeAttribute("data-owner-jarvis-focus"));
  const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(sectionId) : sectionId;
  const node = document.querySelector(`[data-owner-jarvis-section="${escaped}"]`);
  if (!node) return false;
  node.setAttribute("data-owner-jarvis-focus", "true");
  if (scroll) node.scrollIntoView?.({ behavior: "smooth", block: "center", inline: "nearest" });
  window.setTimeout(() => {
    if (node.getAttribute("data-owner-jarvis-focus") === "true") node.removeAttribute("data-owner-jarvis-focus");
  }, Math.max(1200, Number(duration || 0)));
  return true;
}

export function OwnerVoiceOperatorProvider(props) {
  const parentVoice = React.useContext(OwnerVoiceContext);
  if (parentVoice) return props.children;
  return <OwnerVoiceOperatorProviderInner {...props} />;
}

function OwnerVoiceOperatorProviderInner({ introMessage = "", onUnhandledUtterance = null, hasPriorityConversation = null, resetConversationState = null, alwaysOn = false, routeContext = "", children }) {
  const [activeFormId, setActiveFormId] = React.useState("");
  const [currentFieldName, setCurrentFieldName] = React.useState("");
  const [status, setStatus] = React.useState("idle");
  const [recognitionState, setRecognitionState] = React.useState("idle");
  const [isListening, setIsListening] = React.useState(false);
  const [isSpeaking, setIsSpeaking] = React.useState(false);
  const [interimHeard, setInterimHeard] = React.useState("");
  const [message, setMessage] = React.useState("Te escucho.");
  const [lastHeard, setLastHeard] = React.useState("");
  const [confirmation, setConfirmation] = React.useState(false);
  const [handsFree, setHandsFreeState] = React.useState(false);
  const [alwaysOnEnabled, setAlwaysOnEnabledState] = React.useState(() => {
    if (!alwaysOn) return false;
    try { return window.localStorage.getItem("wp-owner-jarvis-always-on") !== "0"; } catch { return true; }
  });
  const [wakeState, setWakeState] = React.useState(() => {
    if (!alwaysOn) return "awake";
    try { return window.localStorage.getItem(OWNER_JARVIS_WAKE_STATE_KEY) === "sleeping" ? "sleeping" : "awake"; } catch { return "awake"; }
  });
  const [needsMicPermission, setNeedsMicPermission] = React.useState(false);
  const [microphoneState, setMicrophoneState] = React.useState("unknown");
  const [microphoneError, setMicrophoneError] = React.useState("");
  const [autoRead, setAutoRead] = React.useState(() => {
    try { return window.localStorage.getItem("wp-owner-voice-auto-read") !== "0"; } catch { return true; }
  });
  const [speechRate, setSpeechRate] = React.useState(() => {
    try {
      const stored = Number(window.localStorage.getItem("wp-owner-voice-rate"));
      return [1, 1.15, 1.3, 1.5, 1.7].includes(stored) ? stored : 1.5;
    } catch { return 1.5; }
  });


  const formGetterRef = React.useRef(null);
  const activeFormIdRef = React.useRef("");
  const currentFieldRef = React.useRef("");
  const recognitionRef = React.useRef(null);
  const recognitionStateRef = React.useRef({ state: "idle", previousState: "idle", operation: "initialize", changedAt: Date.now() });
  const microphoneVerifiedRef = React.useRef(false);
  const microphoneCheckPromiseRef = React.useRef(null);
  const handsFreeRef = React.useRef(false);
  const alwaysOnEnabledRef = React.useRef(alwaysOnEnabled);
  const wakeStateRef = React.useRef(wakeState);
  const pendingFormRef = React.useRef("");
  const processRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  const speechTokenRef = React.useRef(0);
  const recognitionTokenRef = React.useRef(0);
  const pendingListenTimerRef = React.useRef(null);
  const recognitionRestartTimerRef = React.useRef(null);
  const recognitionStartedAtRef = React.useRef(0);
  const recognitionRestartRef = React.useRef({ rapidEnds: 0, lastReportAt: 0 });
  const pendingTranscriptRef = React.useRef("");
  const lastQueuedVoiceFinalRef = React.useRef({ text: "", at: 0 });
  const lastVoiceInputRef = React.useRef({ text: "", at: 0 });
  const recognitionModeRef = React.useRef("handsfree");
  const intentionalRecognitionStopRef = React.useRef(false);
  const currentSpeechTextRef = React.useRef("");
  const speechStartedAtRef = React.useRef(0);
  const isSpeakingRef = React.useRef(false);
  const continuationRef = React.useRef(null);
  const actionContextRef = React.useRef(null);
  const queuedActionAfterSaveRef = React.useRef("");
  const utteranceQueueRef = React.useRef(Promise.resolve());
  const conversationHandlerRef = React.useRef(null);
  const previousRouteContextRef = React.useRef(routeContext);
  const lastMeaningfulSpeechRef = React.useRef("");

  const speechSupported = isOwnerLocalSttSupported();
  const speakSupported = typeof window !== "undefined" && Boolean(window.speechSynthesis && window.SpeechSynthesisUtterance);

  React.useEffect(() => {
    const clean = String(message || "").trim();
    if (!clean || /^(?:te escucho\.?|jarvis dormido|dormido|conversacion pausada)$/i.test(clean)) return;
    appendOwnerJarvisConversationMessage("assistant", clean, { path: routeContext });
  }, [message, routeContext]);

  React.useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);
  React.useEffect(() => { alwaysOnEnabledRef.current = alwaysOnEnabled; }, [alwaysOnEnabled]);
  React.useEffect(() => { wakeStateRef.current = wakeState; }, [wakeState]);

  const updateRecognitionState = React.useCallback((nextState, { previousState = "", operation = "" } = {}) => {
    const safeState = String(nextState || "idle");
    const previous = String(previousState || recognitionStateRef.current?.state || "idle");
    recognitionStateRef.current = {
      state: safeState,
      previousState: previous,
      operation: String(operation || ""),
      changedAt: Date.now(),
    };
    setRecognitionState(safeState);
  }, []);

  const reportVoiceLifecycleFailure = React.useCallback(({
    operation = "recognition",
    previousState = "unknown",
    attemptedState = "unknown",
    failureClass = "voice_lifecycle",
    code = "voice_error",
    technicalError = "",
    microphoneState: reportedMicrophoneState = "",
  } = {}) => {
    const recentVoice = lastVoiceInputRef.current;
    const hasRecentVoice = Boolean(recentVoice?.text && Date.now() - Number(recentVoice.at || 0) <= 60000);
    const currentPath = typeof window !== "undefined"
      ? `${window.location.pathname || ""}${window.location.search || ""}`
      : routeContext || "";
    ownerApi.reportJarvisUnhandled({
      utterance: hasRecentVoice ? recentVoice.text : `Ciclo de voz: ${operation}`,
      currentPath: currentPath || routeContext || "",
      inputSource: hasRecentVoice ? "voice" : "system",
      source: "voice_recognition_lifecycle",
      sessionKind: activeFormIdRef.current ? "voice_form" : "voice_idle",
      reason: "voice_lifecycle_failure",
      conversationContext: ownerJarvisConversationContext(),
      runtimeContext: {
        authority: "backend",
        frontendInterpreter: false,
        activeFormId: activeFormIdRef.current || "",
        currentFieldName: currentFieldRef.current || "",
        wakeState: wakeStateRef.current || "",
        priorityConversation: Boolean(hasPriorityConversation?.()),
        routeContext: routeContext || "",
        voiceFailure: {
          operation: String(operation || "recognition"),
          previousState: String(previousState || "unknown"),
          attemptedState: String(attemptedState || "unknown"),
          failureClass: String(failureClass || "voice_lifecycle"),
          code: String(code || "voice_error"),
          technicalError: String(technicalError || "").slice(0, 700),
          recognitionState: String(recognitionStateRef.current?.state || "unknown"),
          microphoneState: String(reportedMicrophoneState || ""),
          visibilityState: typeof document !== "undefined" ? String(document.visibilityState || "") : "",
        },
      },
    }).catch(() => undefined);
  }, [hasPriorityConversation, routeContext]);

  React.useEffect(() => () => {
    mountedRef.current = false;
    speechTokenRef.current += 1;
    recognitionTokenRef.current += 1;
    if (pendingListenTimerRef.current) window.clearTimeout(pendingListenTimerRef.current);
    if (recognitionRestartTimerRef.current) window.clearTimeout(recognitionRestartTimerRef.current);
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      recognition.onstart = null;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      recognition.onstatechange = null;
      try { recognition.abort?.(); } catch { /* noop */ }
    }
    window.speechSynthesis?.cancel?.();
  }, []);

  const setHandsFree = React.useCallback((value) => {
    handsFreeRef.current = Boolean(value);
    setHandsFreeState(Boolean(value));
  }, []);

  const ensureMicrophoneAccess = React.useCallback(async ({ force = false } = {}) => {
    if (microphoneVerifiedRef.current && !force) return true;
    if (microphoneCheckPromiseRef.current) return microphoneCheckPromiseRef.current;

    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setMicrophoneState("error");
      setMicrophoneError("Este navegador no permite capturar audio para el motor local de Jarvis.");
      return false;
    }

    setMicrophoneState("checking");
    setMicrophoneError("");
    const pending = (async () => {
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const tracks = stream?.getAudioTracks?.() || [];
        if (!tracks.length || tracks.every((track) => track.readyState === "ended")) {
          const error = new Error("No live audio track");
          error.name = "NotReadableError";
          throw error;
        }
        microphoneVerifiedRef.current = true;
        setMicrophoneState("ready");
        setMicrophoneError("");
        setNeedsMicPermission(false);
        return true;
      } catch (error) {
        microphoneVerifiedRef.current = false;
        const detail = microphoneErrorMessage(error);
        const previousState = recognitionStateRef.current?.state || "idle";
        setMicrophoneState("error");
        setMicrophoneError(detail);
        setStatus("error");
        setIsListening(false);
        setNeedsMicPermission(["NotAllowedError", "SecurityError"].includes(String(error?.name || "")));
        setMessage(detail);
        reportVoiceLifecycleFailure({
          operation: "verify_microphone",
          previousState,
          attemptedState: "microphone_ready",
          failureClass: "voice_microphone",
          code: String(error?.name || "microphone_access_failed"),
          technicalError: voiceTechnicalError(error),
          microphoneState: "error",
        });
        return false;
      } finally {
        stopMediaStream(stream);
        microphoneCheckPromiseRef.current = null;
      }
    })();

    microphoneCheckPromiseRef.current = pending;
    return pending;
  }, [reportVoiceLifecycleFailure]);

  const currentConfig = React.useCallback(() => formGetterRef.current?.() || null, []);

  const disposeRecognition = React.useCallback(({ abort = true, intentional = true } = {}) => {
    recognitionTokenRef.current += 1;
    const disposeToken = recognitionTokenRef.current;
    pendingTranscriptRef.current = "";
    intentionalRecognitionStopRef.current = intentional;
    if (intentional) {
      recognitionStartedAtRef.current = 0;
      recognitionRestartRef.current.rapidEnds = 0;
      if (recognitionRestartTimerRef.current) {
        window.clearTimeout(recognitionRestartTimerRef.current);
        recognitionRestartTimerRef.current = null;
      }
    }
    if (pendingListenTimerRef.current) {
      window.clearTimeout(pendingListenTimerRef.current);
      pendingListenTimerRef.current = null;
    }
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    setIsListening(false);
    setInterimHeard("");
    if (!recognition) {
      updateRecognitionState("idle", { operation: abort ? "cancel" : "stop" });
      return undefined;
    }
    const previousState = recognitionStateRef.current?.state || recognition.state || "unknown";
    updateRecognitionState(abort ? "cancelling" : "stopping", { previousState, operation: abort ? "cancel" : "stop" });
    recognition.onstart = null;
    recognition.onend = null;
    recognition.onerror = null;
    recognition.onresult = null;
    recognition.onstatechange = null;
    try {
      const operation = abort ? "cancel" : "stop";
      const transitionalState = abort ? "cancelling" : "stopping";
      const pending = abort ? recognition.abort?.() : recognition.stop?.();
      Promise.resolve(pending).then(() => {
        if (!mountedRef.current || recognitionTokenRef.current !== disposeToken || recognitionRef.current) return;
        updateRecognitionState("idle", { previousState: transitionalState, operation });
      }, (error) => {
        if (!mountedRef.current || recognitionTokenRef.current !== disposeToken || recognitionRef.current) return;
        updateRecognitionState("error", { previousState: transitionalState, operation });
        reportVoiceLifecycleFailure({
          operation,
          previousState: transitionalState,
          attemptedState: "idle",
          code: String(error?.name || "voice_dispose_failed"),
          technicalError: voiceTechnicalError(error),
        });
      });
      return pending;
    } catch (error) {
      updateRecognitionState("error", { previousState, operation: abort ? "cancel" : "stop" });
      reportVoiceLifecycleFailure({
        operation: abort ? "cancel" : "stop",
        previousState,
        attemptedState: "idle",
        code: String(error?.name || "voice_dispose_failed"),
        technicalError: voiceTechnicalError(error),
      });
      return undefined;
    }
  }, [reportVoiceLifecycleFailure, updateRecognitionState]);

  const stopListening = React.useCallback(() => {
    disposeRecognition({ abort: false, intentional: true });
    if (!isSpeakingRef.current) setStatus("idle");
  }, [disposeRecognition]);

  const cancelSpeechForBargeIn = React.useCallback(() => {
    if (!isSpeakingRef.current) return;
    speechTokenRef.current += 1;
    currentSpeechTextRef.current = "";
    isSpeakingRef.current = false;
    setIsSpeaking(false);
    window.speechSynthesis?.cancel?.();
    setStatus(recognitionRef.current ? "listening" : "idle");
  }, []);

  const enqueueTranscript = React.useCallback((transcript) => {
    const clean = String(transcript || "").trim();
    if (!clean) return;

    // El transporte de voz puede entregar dos finales iguales en una misma respuesta.
    // Si el primer final ya avanzó el wizard, procesar el segundo contra el campo
    // siguiente hace que el formulario parezca retroceder o saltear pasos.
    const normalized = normalizeVoiceText(clean);
    const now = Date.now();
    const previous = lastQueuedVoiceFinalRef.current;
    if (normalized && previous.text === normalized && now - previous.at < 1800) return;
    lastQueuedVoiceFinalRef.current = { text: normalized, at: now };
    lastVoiceInputRef.current = { text: clean, at: now };

    utteranceQueueRef.current = utteranceQueueRef.current
      .catch(() => undefined)
      .then(() => processRef.current?.(clean, { inputSource: "voice" }));
  }, []);

  const startListening = React.useCallback(({ oneShot = false } = {}) => {
    if (!speechSupported || !mountedRef.current) {
      setMessage("Este navegador no ofrece un reconocimiento de voz compatible y seguro para este dispositivo. Podés seguir usando Jarvis por texto.");
      setHandsFree(false);
      return;
    }
    if (recognitionRef.current) return;

    intentionalRecognitionStopRef.current = false;
    recognitionModeRef.current = oneShot ? "oneshot" : "handsfree";
    const sessionToken = recognitionTokenRef.current + 1;
    recognitionTokenRef.current = sessionToken;
    if (recognitionRestartTimerRef.current) {
      window.clearTimeout(recognitionRestartTimerRef.current);
      recognitionRestartTimerRef.current = null;
    }

    const scheduleUnexpectedRestart = ({ reason = "end", previousState = "unknown" } = {}) => {
      if (!mountedRef.current
        || intentionalRecognitionStopRef.current
        || !alwaysOnEnabledRef.current
        || recognitionModeRef.current !== "handsfree"
        || !(handsFreeRef.current || wakeStateRef.current === "sleeping")) return;

      const now = Date.now();
      const activeMs = recognitionStartedAtRef.current ? Math.max(0, now - recognitionStartedAtRef.current) : 0;
      const restartState = recognitionRestartRef.current;
      if (activeMs >= VOICE_RESTART_HEALTHY_SESSION_MS) restartState.rapidEnds = 0;
      else restartState.rapidEnds += 1;
      recognitionStartedAtRef.current = 0;

      const rapidEnds = Math.max(1, restartState.rapidEnds);
      const delay = Math.min(
        VOICE_RESTART_BASE_DELAY_MS * (2 ** Math.min(rapidEnds - 1, 6)),
        VOICE_RESTART_MAX_DELAY_MS,
      );

      if (rapidEnds >= VOICE_RESTART_REPORT_AFTER && now - Number(restartState.lastReportAt || 0) >= VOICE_RESTART_REPORT_COOLDOWN_MS) {
        restartState.lastReportAt = now;
        reportVoiceLifecycleFailure({
          operation: "restart",
          previousState,
          attemptedState: "listening",
          failureClass: "voice_restart_loop",
          code: `rapid_${String(reason || "end")}_restart`,
          technicalError: `Reconocimiento reiniciado ${rapidEnds} veces con sesiones menores a ${VOICE_RESTART_HEALTHY_SESSION_MS}ms. Próximo intento en ${delay}ms.`,
          microphoneState: microphoneVerifiedRef.current ? "ready" : "unknown",
        });
      }

      if (recognitionRestartTimerRef.current) window.clearTimeout(recognitionRestartTimerRef.current);
      recognitionRestartTimerRef.current = window.setTimeout(() => {
        recognitionRestartTimerRef.current = null;
        if (!mountedRef.current
          || intentionalRecognitionStopRef.current
          || !alwaysOnEnabledRef.current
          || document.visibilityState === "hidden"
          || recognitionRef.current
          || isSpeakingRef.current
          || recognitionModeRef.current !== "handsfree"
          || !(handsFreeRef.current || wakeStateRef.current === "sleeping")) return;
        startListening({ oneShot: false });
      }, delay);
    };

    try {
      const recognition = new OwnerLocalSpeechRecognition();
      recognition.lang = "es-AR";
      recognition.continuous = !oneShot;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      pendingTranscriptRef.current = "";

      const isCurrent = () => mountedRef.current
        && recognitionTokenRef.current === sessionToken
        && recognitionRef.current === recognition;

      recognition.onstatechange = (event) => {
        if (!isCurrent()) return;
        updateRecognitionState(event?.state || recognition.state || "idle", {
          previousState: event?.previousState || recognitionStateRef.current?.state || "idle",
          operation: event?.operation || "recognition",
        });
      };

      recognition.onstart = () => {
        if (!isCurrent()) return;
        microphoneVerifiedRef.current = true;
        recognitionStartedAtRef.current = Date.now();
        setMicrophoneState("ready");
        setMicrophoneError("");
        setNeedsMicPermission(false);
        setIsListening(true);
        if (!isSpeakingRef.current) setStatus(wakeStateRef.current === "sleeping" ? "sleeping" : "listening");
      };

      recognition.onresult = (event) => {
        if (!isCurrent()) return;
        let interim = "";
        const finals = [];
        for (let index = event.resultIndex || 0; index < (event.results?.length || 0); index += 1) {
          const row = event.results[index];
          const transcript = row?.[0]?.transcript?.trim?.() || "";
          if (!transcript) continue;
          if (row.isFinal) finals.push(transcript);
          else interim += `${interim ? " " : ""}${transcript}`;
        }

        if (interim) {
          if (wakeStateRef.current === "sleeping") {
            // No mostramos ni procesamos conversaciones ambientales mientras duerme.
            // Sólo dejamos una pista visual si parece que el usuario está llamando a Jarvis.
            setInterimHeard(/\bjarvis\b/.test(normalizeVoiceText(interim)) ? interim : "");
          } else {
            setInterimHeard(interim);
            const interimWords = normalizeVoiceText(interim).split(" ").filter(Boolean);
            if (isSpeakingRef.current && interimWords.length >= 2 && !looksLikePromptEcho(interim, currentSpeechTextRef.current)) {
              cancelSpeechForBargeIn();
              setMessage(`Te escucho · ${interim}`);
            }
          }
        }

        if (finals.length) {
          const transcript = finals.join(" ").trim();
          if (!transcript) return;
          if (isSpeakingRef.current && looksLikePromptEcho(transcript, currentSpeechTextRef.current)) return;
          if (isSpeakingRef.current) cancelSpeechForBargeIn();

          // En espera = hotword real. El reconocimiento puede seguir abierto para
          // detectar "hola Jarvis", pero ninguna otra frase entra al runtime.
          if (wakeStateRef.current === "sleeping" && !isJarvisWakePhrase(transcript)) {
            setInterimHeard("");
            setStatus("sleeping");
            return;
          }

          setInterimHeard("");
          recognitionRestartRef.current.rapidEnds = 0;
          setLastHeard(transcript);
          setStatus("processing");
          enqueueTranscript(transcript);
          if (oneShot) {
            intentionalRecognitionStopRef.current = true;
            try { recognition.stop?.(); } catch { /* noop */ }
          } else if (!isSpeakingRef.current) {
            setStatus(wakeStateRef.current === "sleeping" ? "sleeping" : "listening");
          }
        }
      };

      recognition.onend = (event = {}) => {
        if (!isCurrent()) return;
        const previousState = event?.previousState || recognitionStateRef.current?.state || recognition.state || "unknown";
        recognitionRef.current = null;
        if (recognitionStateRef.current?.state !== "error") {
          updateRecognitionState("idle", { previousState, operation: "end" });
        }
        setIsListening(false);
        setInterimHeard("");
        if (!mountedRef.current) return;
        const shouldRestart = alwaysOnEnabledRef.current
          && (handsFreeRef.current || wakeStateRef.current === "sleeping")
          && recognitionModeRef.current === "handsfree"
          && !intentionalRecognitionStopRef.current;
        if (shouldRestart) {
          scheduleUnexpectedRestart({ reason: "end", previousState });
        } else if (!isSpeakingRef.current) {
          recognitionStartedAtRef.current = 0;
          setStatus(wakeStateRef.current === "sleeping" && alwaysOnEnabledRef.current ? "sleeping" : "idle");
        }
      };

      recognition.onerror = (event) => {
        if (!isCurrent()) return;
        const error = event?.error || "";
        recognitionRef.current = null;
        setIsListening(false);
        setInterimHeard("");
        if (!mountedRef.current) return;
        if (error === "aborted") return;
        if (error === "no-speech") {
          if (alwaysOnEnabledRef.current && (handsFreeRef.current || wakeStateRef.current === "sleeping") && !intentionalRecognitionStopRef.current) {
            scheduleUnexpectedRestart({
              reason: "no_speech",
              previousState: event?.lifecycle?.previousState || recognitionStateRef.current?.state || recognition.state || "unknown",
            });
          }
          return;
        }

        const detail = recognitionErrorMessage(error, microphoneVerifiedRef.current, event?.detail);
        const lifecycle = event?.lifecycle || {};
        updateRecognitionState("error", {
          previousState: lifecycle.previousState || recognitionStateRef.current?.state || recognition.state || "unknown",
          operation: lifecycle.operation || "recognition",
        });
        reportVoiceLifecycleFailure({
          operation: lifecycle.operation || "recognition",
          previousState: lifecycle.previousState || "unknown",
          attemptedState: lifecycle.attemptedState || "idle",
          failureClass: error === "local-stt" ? "voice_transcription" : "voice_lifecycle",
          code: String(event?.code || event?.detail?.code || error || "recognition_error"),
          technicalError: voiceTechnicalError(event?.detail),
          microphoneState: error === "audio-capture" ? "error" : microphoneVerifiedRef.current ? "ready" : "unknown",
        });
        setStatus("error");
        setMicrophoneError(detail);

        if (error === "audio-capture") {
          microphoneVerifiedRef.current = false;
          setMicrophoneState("error");
          setHandsFree(false);
          setNeedsMicPermission(false);
        } else if (error === "not-allowed") {
          setHandsFree(false);
          setNeedsMicPermission(!microphoneVerifiedRef.current);
        } else if (error === "local-stt") {
          // Un fallo de carga/inferencia del modelo no se resuelve reiniciando el micrófono en loop.
          setHandsFree(false);
          setNeedsMicPermission(false);
        }

        setMessage(detail);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      const previousState = recognitionStateRef.current?.state || "idle";
      recognitionRef.current = null;
      setIsListening(false);
      updateRecognitionState("error", { previousState, operation: "start" });
      setStatus("error");
      const detail = `No pude iniciar el motor local de voz${error?.name ? ` (${error.name})` : ""}.`;
      setMicrophoneError(detail);
      setNeedsMicPermission(false);
      setMessage(`${detail} Probá Reintentar micrófono.`);
      reportVoiceLifecycleFailure({
        operation: "start",
        previousState,
        attemptedState: "listening",
        code: String(error?.name || "recognition_start_failed"),
        technicalError: voiceTechnicalError(error),
      });
    }
  }, [cancelSpeechForBargeIn, enqueueTranscript, reportVoiceLifecycleFailure, setHandsFree, speechSupported, updateRecognitionState]);

  React.useEffect(() => {
    const previous = previousRouteContextRef.current;
    previousRouteContextRef.current = routeContext;
    if (!previous || previous === routeContext) return undefined;

    // Cambiar de pantalla no debe dejar una captura local de voz vieja
    // colgada. Reciclamos sólo el transporte de voz: la conversación de negocio
    // sigue viva en el runtime global salvo que el usuario haya pedido Inicio.
    pendingTranscriptRef.current = "";
    setInterimHeard("");
    if (!alwaysOnEnabledRef.current || !speechSupported) return undefined;

    disposeRecognition({ abort: true, intentional: true });
    setHandsFree(wakeStateRef.current !== "sleeping");
    const timer = window.setTimeout(() => {
      if (!mountedRef.current || !alwaysOnEnabledRef.current || document.visibilityState === "hidden") return;
      if (!recognitionRef.current && !isSpeakingRef.current) startListening({ oneShot: false });
    }, 140);
    return () => window.clearTimeout(timer);
  }, [disposeRecognition, routeContext, setHandsFree, speechSupported, startListening]);

  const scheduleListening = React.useCallback((delay = 60) => {
    if (pendingListenTimerRef.current) window.clearTimeout(pendingListenTimerRef.current);
    pendingListenTimerRef.current = window.setTimeout(() => {
      pendingListenTimerRef.current = null;
      if (document.visibilityState === "hidden") return;
      if (!alwaysOnEnabledRef.current && !handsFreeRef.current) return;
      if (!recognitionRef.current) {
        const wakeOnly = wakeStateRef.current === "sleeping";
        startListening({ oneShot: wakeOnly ? false : !handsFreeRef.current });
      }
    }, delay);
  }, [startListening]);


  const speak = React.useCallback((text, { listenAfter = false, force = false, onStart = null, onEnd = null, allowWhileSleeping = false } = {}) => {
    const clean = cleanOwnerSpokenText(text);
    if (!clean) return false;
    appendOwnerJarvisConversationMessage("assistant", clean, { path: routeContext });
    setMessage(clean);
    if (!/^(?:te escucho|jarvis dormido|dormido|conversacion pausada|listo\.?$|sí, decime\.?$|si, decime\.?$|hola\.|de nada\.|todo bien\.)/i.test(clean)) lastMeaningfulSpeechRef.current = clean;

    if (wakeStateRef.current === "sleeping" && !allowWhileSleeping) {
      if (alwaysOnEnabledRef.current && !recognitionRef.current) scheduleListening(40);
      return false;
    }

    // Robustez > barge-in automático: mientras Jarvis habla detenemos la captura local
    // para que no se escuche a sí mismo. El usuario siempre puede cortar la locución
    // con "Silenciar bot", y al terminar reabrimos el micrófono automáticamente.
    if (recognitionRef.current) disposeRecognition({ abort: true, intentional: true });

    if (!speakSupported || (!autoRead && !force)) {
      if (typeof onEnd === "function") onEnd({ spoken: false });
      if ((listenAfter || handsFreeRef.current || wakeStateRef.current === "sleeping") && alwaysOnEnabledRef.current) scheduleListening(30);
      return false;
    }

    const token = speechTokenRef.current + 1;
    speechTokenRef.current = token;
    currentSpeechTextRef.current = clean;
    speechStartedAtRef.current = Date.now();
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.lang = "es-AR";
    const preferredVoice = preferredOwnerSpanishVoice();
    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.rate = speechRate;
    utterance.pitch = 0.96;
    let finished = false;
    utterance.onstart = () => {
      if (!mountedRef.current || speechTokenRef.current !== token) return;
      isSpeakingRef.current = true;
      setIsSpeaking(true);
      setStatus("speaking");
      try { onStart?.(); } catch { /* observer only */ }
    };
    const finishSpeech = () => {
      if (finished) return;
      finished = true;
      if (!mountedRef.current || speechTokenRef.current !== token) return;
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      currentSpeechTextRef.current = "";
      const shouldResume = alwaysOnEnabledRef.current
        && (listenAfter || handsFreeRef.current || wakeStateRef.current === "sleeping");
      setStatus(wakeStateRef.current === "sleeping" ? "sleeping" : "idle");
      try { onEnd?.({ spoken: true }); } catch { /* observer only */ }
      if (shouldResume && !recognitionRef.current) scheduleListening(80);
    };
    utterance.onend = finishSpeech;
    utterance.onerror = finishSpeech;
    window.speechSynthesis.speak(utterance);
    return true;
  }, [autoRead, disposeRecognition, routeContext, scheduleListening, speakSupported, speechRate]);

  const interruptAndListen = React.useCallback(({ handsFree: keepHandsFree = true } = {}) => {
    if (wakeStateRef.current === "sleeping") {
      wakeStateRef.current = "awake";
      setWakeState("awake");
      try { window.localStorage.setItem(OWNER_JARVIS_WAKE_STATE_KEY, "awake"); } catch { /* optional */ }
    }
    setHandsFree(keepHandsFree);
    cancelSpeechForBargeIn();
    setMessage("Te escucho.");
    if (!recognitionRef.current) startListening({ oneShot: !keepHandsFree });
  }, [cancelSpeechForBargeIn, setHandsFree, startListening]);

  const stopConversation = React.useCallback(() => {
    setHandsFree(false);
    cancelSpeechForBargeIn();
    disposeRecognition({ abort: true, intentional: true });
    queuedActionAfterSaveRef.current = "";
    setStatus("idle");
    setMessage("Conversación pausada. Cuando quieras, seguimos.");
  }, [cancelSpeechForBargeIn, disposeRecognition, setHandsFree]);

  const setWakeStatePersisted = React.useCallback((next) => {
    const safe = next === "sleeping" ? "sleeping" : "awake";
    wakeStateRef.current = safe;
    setWakeState(safe);
    try { window.localStorage.setItem(OWNER_JARVIS_WAKE_STATE_KEY, safe); } catch { /* optional */ }
  }, []);

  const sleepJarvis = React.useCallback(({ announce = true } = {}) => {
    if (!alwaysOnEnabledRef.current) {
      setMessage("Jarvis está apagado.");
      return false;
    }
    setWakeStatePersisted("sleeping");
    setHandsFree(false);
    disposeRecognition({ abort: true, intentional: true });
    setInterimHeard("");
    setStatus("sleeping");
    setMessage("En espera · decí “Hola Jarvis”.");
    if (announce) {
      speak("Listo.", { listenAfter: false, force: true, allowWhileSleeping: true });
    } else {
      scheduleListening(80);
    }
    try { window.dispatchEvent(new CustomEvent("owner:jarvis-sleep")); } catch { /* optional */ }
    return true;
  }, [disposeRecognition, scheduleListening, setHandsFree, setWakeStatePersisted, speak]);

  const wakeJarvis = React.useCallback(async ({ announce = true } = {}) => {
    setAlwaysOnEnabledState(true);
    alwaysOnEnabledRef.current = true;
    try { window.localStorage.setItem("wp-owner-jarvis-always-on", "1"); } catch { /* noop */ }
    const micReady = await ensureMicrophoneAccess({ force: microphoneState === "error" });
    if (!micReady) return false;
    setWakeStatePersisted("awake");
    setHandsFree(true);
    setNeedsMicPermission(false);
    setMicrophoneError("");
    setInterimHeard("");
    setMessage("Te escucho.");
    disposeRecognition({ abort: true, intentional: true });
    if (announce) {
      speak("Sí, decime.", { listenAfter: true, force: true });
    } else if (!recognitionRef.current) {
      startListening({ oneShot: false });
    }
    window.setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent("owner:jarvis-wake")); } catch { /* optional */ }
    }, 0);
    return true;
  }, [disposeRecognition, ensureMicrophoneAccess, microphoneState, setHandsFree, setWakeStatePersisted, speak, startListening]);


  const askField = React.useCallback((fieldName, { forceListen = false } = {}) => {
    const config = currentConfig();
    if (!config) return;
    const fields = visibleFields(config);
    const field = fields.find((row) => row.name === fieldName) || fields[0];
    if (!field) {
      const missing = requiredMissing(config);
      if (missing.length) {
        setCurrentFieldName(missing[0].name);
        currentFieldRef.current = missing[0].name;
        focusVoiceField(missing[0].name);
        speak(`${missing[0].prompt || `¿${missing[0].label || missing[0].name}?`}${formatOptions(missing[0])}`, { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
        return;
      }
      setConfirmation(true);
      speak("Tengo lo necesario. ¿Lo guardo?", { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
      return;
    }
    setConfirmation(false);
    setCurrentFieldName(field.name);
    currentFieldRef.current = field.name;
    focusVoiceField(field.name);
    const value = config.values?.[field.name];
    const current = hasValue(value) && field.announceCurrent ? ` Ahora figura ${fieldValueLabel(field, value)}.` : "";
    speak(`${field.prompt || `¿${field.label || field.name}?`}${current}${formatOptions(field)}`, { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
  }, [currentConfig, speak]);

  const nextField = React.useCallback((direction = 1, { forceListen = false } = {}) => {
    const config = currentConfig();
    if (!config) return;
    const fields = visibleFields(config);
    if (!fields.length) return askField("", { forceListen });
    const currentName = currentFieldRef.current;
    let index = fields.findIndex((row) => row.name === currentName);
    if (index < 0) index = direction > 0 ? -1 : fields.length;
    let nextIndex = index + direction;
    while (nextIndex >= 0 && nextIndex < fields.length) {
      const field = fields[nextIndex];
      if (!(field.skipIfFilled && hasValue(config.values?.[field.name]))) return askField(field.name, { forceListen });
      nextIndex += direction;
    }
    if (direction < 0) return askField(fields[0].name, { forceListen });
    const missing = requiredMissing(config);
    if (missing.length) return askField(missing[0].name, { forceListen });
    setConfirmation(true);
    setCurrentFieldName("");
    currentFieldRef.current = "";
    speak("Tengo lo necesario. ¿Lo guardo?", { listenAfter: forceListen || handsFreeRef.current, force: forceListen });
  }, [askField, currentConfig, speak]);

  const beginCurrentForm = React.useCallback(({ handsFree: requestedHandsFree = true } = {}) => {
    if (requestedHandsFree && wakeStateRef.current === "sleeping") {
      wakeStateRef.current = "awake";
      setWakeState("awake");
      try { window.localStorage.setItem(OWNER_JARVIS_WAKE_STATE_KEY, "awake"); } catch { /* optional */ }
    }
    const config = currentConfig();
    if (!config) {
      setHandsFree(requestedHandsFree);
      speak("Decime qué querés hacer.", { listenAfter: requestedHandsFree, force: true });
      return;
    }
    setHandsFree(requestedHandsFree);
    const fields = visibleFields(config);
    const first = fields.find((field) => !field.skipIfFilled || !hasValue(config.values?.[field.name])) || fields[0];
    if (!first) return;
    askField(first.name, { forceListen: requestedHandsFree });
  }, [askField, currentConfig, setHandsFree, speak]);

  const registerForm = React.useCallback((id, getter) => {
    activeFormIdRef.current = id;
    formGetterRef.current = getter;
    setActiveFormId(id);
    setConfirmation(false);
    const pending = pendingFormRef.current;
    if (pending && pending === id) {
      pendingFormRef.current = "";
      window.setTimeout(() => beginCurrentForm({ handsFree: handsFreeRef.current }), 35);
    }
    return () => {
      if (activeFormIdRef.current !== id) return;
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      setConfirmation(false);
      document.querySelectorAll?.('[data-owner-voice-active="true"]').forEach((node) => node.removeAttribute("data-owner-voice-active"));
    };
  }, [beginCurrentForm]);

  const registerConversationHandler = React.useCallback((handler) => {
    conversationHandlerRef.current = typeof handler === "function" ? handler : null;
    return () => {
      if (conversationHandlerRef.current === handler) conversationHandlerRef.current = null;
    };
  }, []);



  const resetOwnerConversation = React.useCallback(async ({ navigateHome = false, announce = true } = {}) => {
    const config = currentConfig();
    try { await config?.onCancel?.(); } catch { /* reset nunca debe quedar bloqueado por un diálogo */ }

    activeFormIdRef.current = "";
    formGetterRef.current = null;
    currentFieldRef.current = "";
    pendingFormRef.current = "";
    continuationRef.current = null;
    actionContextRef.current = null;
    queuedActionAfterSaveRef.current = "";
    pendingTranscriptRef.current = "";
    setActiveFormId("");
    setCurrentFieldName("");
    setConfirmation(false);
    setInterimHeard("");
    setStatus(recognitionRef.current ? "listening" : "idle");
    resetConversationState?.();

    clearOwnerJarvisStoredTurns();
    clearOwnerJarvisConversationJournal();
    try { window.dispatchEvent(new CustomEvent("owner:jarvis-conversation-reset", { detail: { navigateHome } })); } catch { /* noop */ }

    if (navigateHome) {
      try { window.dispatchEvent(new CustomEvent("owner:jarvis-navigate-home")); } catch { /* optional */ }
      if (announce) speak("Volví a Inicio. La conversación anterior quedó cerrada.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return true;
    }
    if (announce) speak("Reinicié la conversación. Decime qué querés resolver.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
    else setMessage("¿Qué querés hacer?");
    return true;
  }, [currentConfig, resetConversationState, speak]);





  const submitCurrentForm = React.useCallback(async () => {
    const config = currentConfig();
    if (!config) return false;
    const missing = requiredMissing(config);
    if (missing.length) {
      askField(missing[0].name, { forceListen: handsFreeRef.current });
      return false;
    }

    setStatus("saving");
    setMessage(`Guardando ${config.title}…`);
    const previousContext = actionContextRef.current || continuationRef.current;
    try {
      const result = await config.onSubmit?.();
      if (result === false) {
        setStatus("error");
        speak("No pude guardarlo todavía. Mirá qué quedó marcado y lo corregimos.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return false;
      }

      setConfirmation(false);
      // Liberamos el formulario en la capa de voz inmediatamente. La UI puede
      // cerrar unos milisegundos después, pero la próxima orden ya se interpreta
      // como acción global/contextual y no como dato del formulario anterior.
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      document.querySelectorAll?.('[data-owner-voice-active="true"]').forEach((node) => node.removeAttribute("data-owner-voice-active"));
      let continuation = null;
      if (typeof config.afterSubmit === "function") continuation = config.afterSubmit(result, previousContext) || null;
      else if (config.afterSubmit) continuation = config.afterSubmit;

      if (continuation?.context !== undefined) {
        continuationRef.current = continuation.context;
        actionContextRef.current = continuation.context;
      } else if (previousContext) {
        continuationRef.current = previousContext;
        actionContextRef.current = previousContext;
      }

      const context = continuationRef.current;
      setStatus(recognitionRef.current ? "listening" : "idle");

      const fallbackPrompt = context
        ? `Listo. Seguimos con ${contextLabel(context)} cuando quieras.`
        : `${config.successMessage || "Listo."}`;
      const prompt = continuation?.prompt || fallbackPrompt;
      if (handsFreeRef.current) speak(prompt, { listenAfter: true, force: true });
      else setMessage(prompt);
      return true;
    } catch {
      setStatus("error");
      speak("No pude guardarlo todavía. Corregimos lo que falte y probamos de nuevo.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return false;
    }
  }, [askField, currentConfig, speak]);

  const processUtterance = React.useCallback(async (raw, meta = {}) => {
    const text = normalizeVoiceText(raw);
    if (!text) return;
    appendOwnerJarvisConversationMessage("user", raw, { inputSource: meta.inputSource || "voice", path: routeContext });

    // Controles de presencia: locales, instantáneos y sin IA.
    if (isJarvisSleepPhrase(text)) {
      sleepJarvis({ announce: true });
      return;
    }
    if (isJarvisWakePhrase(text)) {
      const remainder = jarvisWakeRemainder(raw);
      const needsWake = wakeStateRef.current === "sleeping" || !alwaysOnEnabledRef.current;
      if (needsWake) await wakeJarvis({ announce: !remainder });
      if (remainder) {
        // "Hola Jarvis, abrime Memorial" despierta y conserva la intención.
        window.setTimeout(() => processRef.current?.(remainder, { inputSource: meta.inputSource || "voice" }), 60);
      } else if (!needsWake) {
        speak("Sí, decime.", { listenAfter: true, force: true });
      }
      return;
    }
    if (meta.inputSource === "voice" && wakeStateRef.current === "sleeping") return;
    if (isJarvisGreeting(text)) {
      speak("Hola. Decime.", { listenAfter: true, force: true });
      return;
    }
    if (isJarvisHowAreYou(text)) {
      speak("Todo bien. Decime.", { listenAfter: true, force: true });
      return;
    }
    if (isJarvisThanks(text)) {
      speak("De nada.", { listenAfter: true, force: true });
      return;
    }
    if (isJarvisWhereWereWe(text)) {
      const recap = String(lastMeaningfulSpeechRef.current || "").trim();
      speak(recap && !/^sí, decime|^hola\.|^de nada\./i.test(recap) ? recap : "No tenemos un objetivo abierto. Decime qué querés resolver.", { listenAfter: true, force: true });
      return;
    }
    if (matchesCommand(text, COMMANDS.stop)) {
      setAlwaysOnEnabledState(false);
      alwaysOnEnabledRef.current = false;
      try { window.localStorage.setItem("wp-owner-jarvis-always-on", "0"); } catch { /* noop */ }
      stopConversation();
      setMessage("Voz detenida. Tocá el micrófono para volver a activarla.");
      return;
    }
    if (isOwnerHomeResetIntent(text)) {
      await resetOwnerConversation({ navigateHome: true, announce: true });
      return;
    }

    if (matchesCommand(text, COMMANDS.repeat)) {
      const repeatConfig = currentConfig();
      if (repeatConfig && currentFieldRef.current) {
        const repeatField = visibleFields(repeatConfig).find((row) => row.name === currentFieldRef.current);
        if (repeatField) {
          speak(`${repeatField.prompt || repeatField.label}.${formatOptions(repeatField)}`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
          return;
        }
      }
      speak(message || "¿Qué querés hacer?", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }

    const config = currentConfig();
    if (!config) {
      if (matchesCommand(text, COMMANDS.options)) {
        const continuation = continuationRef.current;
        if (continuation?.prompt) speak(continuation.prompt, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        else speak("Hablame normal. Podés pedirme que revisemos proyectos, pendientes, cobros, seguimientos o tu día; también podés decirme que abra algo, que anote una tarea, un gasto, una persona o una propuesta.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
      // Autoridad única: cuando no hay un formulario activo, el frontend no
      // interpreta intención, aliases, navegación ni shortcuts. Todo lenguaje
      // natural se resuelve en Jarvis backend y la UI sólo ejecuta el contrato.
      if (typeof conversationHandlerRef.current === "function") {
        try {
          const conversationResult = await conversationHandlerRef.current({ raw, text, inputSource: meta.inputSource || "voice" });
          if (conversationResult?.handled) {
            if (conversationResult.highlightSection) highlightOwnerSection(conversationResult.highlightSection);
            if (conversationResult.message) speak(conversationResult.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
            return;
          }
        } catch { /* backend runtime below remains authoritative */ }
      }
      if (typeof onUnhandledUtterance === "function") {
        const result = await onUnhandledUtterance({ raw, text, inputSource: meta.inputSource || "voice" });
        if (result?.handled) {
          if (result.highlightSection) highlightOwnerSection(result.highlightSection);
          if (result.message) speak(result.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
          return;
        }
      }
      const continuation = continuationRef.current;
      if (continuation?.prompt) {
        speak(continuation.prompt, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      } else {
        ownerApi.reportJarvisUnhandled({
          utterance: raw,
          currentPath: routeContext || (typeof window !== "undefined" ? window.location.pathname : ""),
          inputSource: meta.inputSource || "voice",
          source: "voice_runtime_fallback",
          sessionKind: activeFormIdRef.current ? "voice_form" : "voice_idle",
          reason: "no_safe_action",
          observedReply: "No me cerró qué querés dejar hecho. Decímelo como te salga y lo ubico.",
          conversationContext: ownerJarvisConversationContext(),
          runtimeContext: {
            activeFormId: activeFormIdRef.current || "",
            currentFieldName: currentFieldRef.current || "",
            wakeState: wakeStateRef.current || "",
            priorityConversation: Boolean(hasPriorityConversation?.()),
            continuationPrompt: continuationRef.current?.prompt || "",
            routeContext: routeContext || "",
          },
        }).catch(() => undefined);
        speak("No me cerró qué querés dejar hecho. Decímelo como te salga y lo ubico.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      }
      return;
    }

    if (!confirmation && looksLikeGlobalEscapeIntent(text)) {
      // El action registry se usa sólo como señal de que hay un destino/formulario
      // conocido; la resolución real se delega al Jarvis conversacional/backend para
      // no duplicar aliases ni decidir acá entre, por ejemplo, Proyecto y Proyectos.
      await config.onCancel?.();
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      pendingFormRef.current = "";
      continuationRef.current = null;
      actionContextRef.current = null;
      queuedActionAfterSaveRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      setConfirmation(false);
      resetConversationState?.();

      if (typeof conversationHandlerRef.current === "function") {
        try {
          const result = await conversationHandlerRef.current({ raw, text, inputSource: meta.inputSource || "voice" });
          if (result?.handled) {
            if (result.highlightSection) highlightOwnerSection(result.highlightSection);
            if (result.message) speak(result.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
            return;
          }
        } catch {
          // El fallback clásico de abajo conserva la degradación existente.
        }
      }

      if (typeof onUnhandledUtterance === "function") {
        try {
          const result = await onUnhandledUtterance({ raw, text, inputSource: meta.inputSource || "voice" });
          if (result?.handled) {
            if (result.highlightSection) highlightOwnerSection(result.highlightSection);
            if (result.message) speak(result.message, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
            return;
          }
        } catch {
          speak("No pude resolver ese cambio ahora. Solté lo anterior para no mezclar cosas; probemos de nuevo.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
          return;
        }
      }
      speak("Listo, solté lo anterior. Decime qué querés hacer ahora.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }

    if (matchesCommand(text, COMMANDS.cancel)) {
      await config.onCancel?.();
      activeFormIdRef.current = "";
      formGetterRef.current = null;
      currentFieldRef.current = "";
      setActiveFormId("");
      setCurrentFieldName("");
      const continuation = continuationRef.current;
      const prompt = continuation?.prompt || `${config.title} cancelado. ¿Qué querés hacer ahora?`;
      speak(prompt, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    if (matchesCommand(text, COMMANDS.options)) {
      speak("Decime los datos como te salga. Si querés también podés decir qué falta, corregir algo, guardar o cancelar.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    if (matchesCommand(text, COMMANDS.missing)) {
      const missing = requiredMissing(config);
      if (!missing.length) speak("Está todo lo necesario. Si está bien, lo guardamos.", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      else speak(`Falta ${missing.map((field) => field.label || field.name).join(", ")}.`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    if (matchesCommand(text, COMMANDS.previous)) return nextField(-1, { forceListen: handsFreeRef.current });
    if (matchesCommand(text, COMMANDS.next)) {
      const current = visibleFields(config).find((field) => field.name === currentFieldRef.current);
      const required = current && (typeof current.required === "function" ? current.required(config.values || {}) : current.required);
      if (required && !hasValue(config.values?.[current.name])) {
        speak(`${current.prompt || `Me falta ${current.label || current.name}. ¿Cómo es?`}`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
      return nextField(1, { forceListen: handsFreeRef.current });
    }
    if (matchesCommand(text, COMMANDS.startForm)) return beginCurrentForm({ handsFree: true });

    if (confirmation) {
      if (isAffirmative(text)) {
        await submitCurrentForm();
        return;
      }
      if (isNegative(text)) {
        setConfirmation(false);
        speak("Dale. ¿Qué querés cambiar?", { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
    }

    if (matchesCommand(text, COMMANDS.save)) {
      // "Guardar" ya es una orden explícita: no obligamos a confirmar dos veces.
      await submitCurrentForm();
      return;
    }

    const fields = visibleFields(config);
    const explicitField = findFieldByVoice(text, fields);
    let field = explicitField || fields.find((row) => row.name === currentFieldRef.current) || fields[0];
    if (!field) return;

    if (explicitField && explicitField.name !== currentFieldRef.current) {
      setCurrentFieldName(explicitField.name);
      currentFieldRef.current = explicitField.name;
      focusVoiceField(explicitField.name);
      const commandValue = cleanFieldCommand(raw, explicitField);
      if (normalizeVoiceText(commandValue) === text || !commandValue.trim()) {
        speak(`${explicitField.prompt || `¿${explicitField.label}?`}${formatOptions(explicitField)}`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
        return;
      }
      field = explicitField;
    }

    const parsed = parseFieldValue(field, raw, config);
    if (parsed === null || parsed === undefined || (typeof parsed === "number" && !Number.isFinite(parsed))) {
      speak(`No agarré ${field.label || field.name}. ${field.prompt || "¿Cómo es?"}${formatOptions(field)}`, { listenAfter: handsFreeRef.current, force: handsFreeRef.current });
      return;
    }
    config.setValue?.(field.name, parsed);
    setMessage(`${field.label || field.name}: ${fieldValueLabel(field, parsed)}.`);
    window.setTimeout(() => nextField(1, { forceListen: handsFreeRef.current }), 20);
  }, [askField, beginCurrentForm, confirmation, currentConfig, hasPriorityConversation, message, nextField, onUnhandledUtterance, resetConversationState, resetOwnerConversation, routeContext, sleepJarvis, speak, stopConversation, submitCurrentForm, wakeJarvis]);

  processRef.current = processUtterance;

  const sendText = React.useCallback((value) => {
    const clean = String(value || "").trim();
    if (!clean) return;
    setLastHeard(clean);
    processRef.current?.(clean, { inputSource: "text" });
  }, []);

  const enableAlwaysOn = React.useCallback(async () => wakeJarvis({ announce: false }), [wakeJarvis]);

  // Apagado real: se usa sólo cuando el usuario pide dejar de escuchar o si
  // explícitamente deshabilitamos voz. "En espera" mantiene hotword y no representa sueño del Owner.
  const disableAlwaysOn = React.useCallback(() => {
    setAlwaysOnEnabledState(false);
    alwaysOnEnabledRef.current = false;
    try { window.localStorage.setItem("wp-owner-jarvis-always-on", "0"); } catch { /* noop */ }
    setWakeStatePersisted("awake");
    stopConversation();
    setMessage("Voz detenida. Tocá el micrófono para volver a activarla.");
  }, [setWakeStatePersisted, stopConversation]);

  React.useEffect(() => {
    if (!alwaysOn || !alwaysOnEnabled || !speechSupported) return undefined;
    const reengage = () => {
      if (!mountedRef.current || !alwaysOnEnabledRef.current || needsMicPermission || document.visibilityState === "hidden") return;
      const sleeping = wakeStateRef.current === "sleeping";
      setHandsFree(!sleeping);
      setStatus(sleeping ? "sleeping" : "listening");
      setMessage(sleeping ? "En espera · decí “Hola Jarvis”." : "Te escucho.");
      if (!recognitionRef.current && !isSpeakingRef.current) startListening({ oneShot: false });
    };
    const timer = window.setTimeout(reengage, 350);
    const suspendRecognition = () => {
      if (!mountedRef.current) return;
      disposeRecognition({ abort: true, intentional: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        suspendRecognition();
        return;
      }
      window.setTimeout(reengage, 120);
    };
    const onPageHide = () => suspendRecognition();
    const onPageShow = () => window.setTimeout(reengage, 120);
    window.addEventListener("focus", reengage);
    window.addEventListener("online", reengage);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", reengage);
      window.removeEventListener("online", reengage);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [alwaysOn, alwaysOnEnabled, disposeRecognition, needsMicPermission, setHandsFree, speechSupported, startListening, wakeState]);

  const startGlobalConversation = React.useCallback(() => {
    if (alwaysOn) {
      wakeJarvis({ announce: false });
      return;
    }
    setHandsFree(true);
    setMessage("Te escucho.");
    if (!recognitionRef.current) startListening({ oneShot: false });
  }, [alwaysOn, setHandsFree, startListening, wakeJarvis]);

  const toggleOneShotMic = React.useCallback(async () => {
    if (wakeStateRef.current === "sleeping") {
      await wakeJarvis({ announce: false });
      return;
    }
    if (handsFreeRef.current) {
      stopConversation();
      return;
    }
    if (isListening) {
      stopListening();
      return;
    }
    if (isSpeaking) cancelSpeechForBargeIn();
    setHandsFree(false);
    if (activeFormIdRef.current && !currentFieldRef.current) beginCurrentForm({ handsFree: false });
    const micReady = await ensureMicrophoneAccess({ force: microphoneState === "error" });
    if (!micReady) return;
    startListening({ oneShot: true });
  }, [beginCurrentForm, cancelSpeechForBargeIn, ensureMicrophoneAccess, isListening, isSpeaking, microphoneState, setHandsFree, startListening, stopConversation, stopListening, wakeJarvis]);

  const say = React.useCallback((text, { listenAfter = false, force = true, onStart = null, onEnd = null } = {}) => {
    if (listenAfter && wakeStateRef.current === "awake") setHandsFree(true);
    if (!text) return false;
    return speak(text, { listenAfter, force, onStart, onEnd });
  }, [setHandsFree, speak]);

  const guideToSection = React.useCallback((sectionId, text, { listenAfter = false, force = true, scroll = true, onStart = null, onEnd = null } = {}) => {
    highlightOwnerSection(sectionId, { scroll });
    say(text, { listenAfter, force, onStart, onEnd });
  }, [say]);

  const readCurrent = React.useCallback(() => {
    const config = currentConfig();
    if (config && currentFieldRef.current) {
      const field = visibleFields(config).find((row) => row.name === currentFieldRef.current);
      if (field) return speak(`${field.prompt || field.label}.${formatOptions(field)}`, { force: true });
    }
    speak(message || "¿Qué querés hacer?", { force: true });
  }, [currentConfig, message, speak]);

  const changeAutoRead = React.useCallback((value) => {
    setAutoRead(Boolean(value));
    try { window.localStorage.setItem("wp-owner-voice-auto-read", value ? "1" : "0"); } catch { /* noop */ }
  }, []);

  const changeSpeechRate = React.useCallback((value) => {
    const next = Number(value);
    const safe = [1, 1.15, 1.3, 1.5, 1.7].includes(next) ? next : 1.5;
    setSpeechRate(safe);
    try { window.localStorage.setItem("wp-owner-voice-rate", String(safe)); } catch { /* noop */ }
  }, []);

  const priorityConversationActive = React.useCallback(() => Boolean(hasPriorityConversation?.()), [hasPriorityConversation]);

  const value = React.useMemo(() => ({
    activeFormId,
    alwaysOnEnabled,
    autoRead,
    beginCurrentForm,
    changeAutoRead,
    changeSpeechRate,
    confirmation,
    currentConfig,
    currentFieldName,
    disableAlwaysOn,
    enableAlwaysOn,
    handsFree,
    isAwake: alwaysOnEnabled && wakeState === "awake",
    guideToSection,
    interimHeard,
    interruptAndListen,
    isListening,
    isSpeaking,
    lastHeard,
    message,
    microphoneError,
    microphoneState,
    needsMicPermission,
    priorityConversationActive,
    recognitionState,
    resetOwnerConversation,
    readCurrent,
    registerConversationHandler,
    registerForm,
    say,
    sendText,
    sleepJarvis,
    speakSupported,
    speechRate,
    speechSupported,
    startGlobalConversation,
    status,
    stopConversation,
    submitCurrentForm,
    toggleOneShotMic,
    wakeJarvis,
    wakeState,
  }), [activeFormId, alwaysOnEnabled, autoRead, beginCurrentForm, changeAutoRead, changeSpeechRate, confirmation, currentConfig, currentFieldName, disableAlwaysOn, enableAlwaysOn, handsFree, guideToSection, interimHeard, interruptAndListen, isListening, isSpeaking, lastHeard, message, microphoneError, microphoneState, needsMicPermission, priorityConversationActive, readCurrent, recognitionState, registerConversationHandler, registerForm, resetOwnerConversation, say, sendText, sleepJarvis, speakSupported, speechRate, speechSupported, startGlobalConversation, status, stopConversation, submitCurrentForm, toggleOneShotMic, wakeJarvis, wakeState]);

  return (
    <OwnerVoiceContext.Provider value={value}>
      <GlobalStyles styles={{
        '[data-owner-voice-active="true"]': {
          outline: "3px solid rgba(243,90,58,.24)",
          outlineOffset: "3px",
          borderRadius: "10px",
          transition: "outline-color .18s ease",
        },
        '[data-owner-jarvis-focus="true"]': {
          outline: "3px solid rgba(243,90,58,.42)",
          outlineOffset: "4px",
          boxShadow: "0 0 0 8px rgba(243,90,58,.08), 0 18px 50px rgba(20,31,42,.12)",
          transform: "translateY(-1px)",
          transition: "outline-color .2s ease, box-shadow .2s ease, transform .2s ease",
        },
      }} />
      {children}
    </OwnerVoiceContext.Provider>
  );
}

export function useOwnerVoiceOperator() {
  return React.useContext(OwnerVoiceContext);
}

export function useOwnerVoiceForm({ id, open, config }) {
  const voice = useOwnerVoiceOperator();
  const configRef = React.useRef(config);
  configRef.current = config;
  React.useEffect(() => {
    if (!open || !voice?.registerForm || !id) return undefined;
    return voice.registerForm(id, () => configRef.current);
  }, [id, open, voice?.registerForm]);
  return voice;
}

export function voiceFieldProps(name) {
  return { "data-owner-voice-field": name };
}

export function OwnerVoiceFormControls({ label = "Completar por voz" }) {
  const voice = useOwnerVoiceOperator();
  if (!voice) return null;
  const config = voice.currentConfig?.();
  const fieldLabel = config?.fields?.find((field) => field.name === voice.currentFieldName)?.label || voice.currentFieldName;
  return (
    <Box sx={{ mt: .8, p: .9, borderRadius: 2.2, bgcolor: "rgba(243,90,58,.055)", border: "1px solid rgba(243,90,58,.13)" }}>
      <Stack direction="row" spacing={.7} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={voice.handsFree ? "JARVIS · ESCUCHANDO" : "JARVIS"} color={voice.handsFree ? "primary" : "default"} sx={{ height: 22, fontWeight: 1000 }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 760, color: "text.secondary" }}>
            {fieldLabel ? `Te estoy pidiendo: ${fieldLabel}` : "Podés hablarme normal para completar esto."}
          </Typography>
          {voice.handsFree && voice.isListening && !voice.isSpeaking ? <Typography sx={{ fontSize: 11.5, fontWeight: 850, color: "success.main" }}>Escuchando</Typography> : null}
        </Stack>
        <Stack direction="row" spacing={.5}>
          <Tooltip title="Leer indicación"><span><IconButton size="small" disabled={!voice.speakSupported} onClick={voice.readCurrent}><VolumeUpRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          {voice.isSpeaking && voice.handsFree ? (
            <Button size="small" variant="text" onClick={() => voice.interruptAndListen({ handsFree: true })} sx={{ textTransform: "none", fontWeight: 900 }}>Silenciar bot</Button>
          ) : null}
          <Button
            size="small"
            variant={voice.handsFree ? "contained" : "outlined"}
            startIcon={voice.handsFree ? <StopCircleRoundedIcon /> : <MicRoundedIcon />}
            onClick={() => voice.handsFree ? voice.stopConversation() : voice.beginCurrentForm({ handsFree: true })}
            disabled={!voice.speechSupported}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {voice.handsFree ? "Pausar conversación" : label}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
